import * as React from 'react'
import { isDeepEqual } from '@applift/factor'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import { useGridApiMethod } from '../../utils/useGridApiMethod'
import { DataGridProcessedProps } from '../../../models/props/DataGridProps'
import { GridStateInitializer } from '../../utils/useGridInitializeState'
import { GridHeaderGroupDependentsApi } from '../../../models/api/gridHeaderGroupDependentsApi'
import { gridHeaderGroupDependentsSelector } from './headerGroupDependentsSelector'
import {
  ReactTableHeader,
  ReactTableHeaderGroup,
} from '../../../models/gridReactTable'
import type { HeaderGroupDependents } from './headerGroupDependentsState'

const computeAllHeaderGroupDependents = ({
  left,
  right,
  center,
}: {
  left: ReactTableHeaderGroup[]
  right: ReactTableHeaderGroup[]
  center: ReactTableHeaderGroup[]
}) => {
  if (center.length && center.length > 1) {
    const leftResult = computeSingleHeaderGroupDependent(
      left.slice(0, left.length - 1)
    )
    const centerResult = computeSingleHeaderGroupDependent(
      center.slice(0, center.length - 1)
    )
    const rightResult = computeSingleHeaderGroupDependent(
      right.slice(0, right.length - 1)
    )

    return {
      left: leftResult,
      center: centerResult,
      right: rightResult,
    }
  }
  return {
    left: {},
    center: {},
    right: {},
  }
}

const computeSingleHeaderGroupDependent = (
  headerGroup: ReactTableHeaderGroup[]
): HeaderGroupDependents => {
  const accum = {} as HeaderGroupDependents
  for (let i = 0; i < headerGroup.length; i++) {
    const one = headerGroup[i]
    one?.headers.reduce((prev, curr) => {
      // @ts-ignore
      prev[curr.id] = computeHeaderGroupDependents(curr, curr.id)
      return prev
    }, accum)
  }
  return accum
}

const computeHeaderGroupDependents = (
  header: ReactTableHeader,
  id: string,
  result?: string[]
) => {
  if (!Array.isArray(result)) {
    result = []
  }
  if (header.subHeaders.length) {
    for (let i = 0; i < header.subHeaders.length; i++) {
      const one = header.subHeaders[i]!
      computeHeaderGroupDependents(one, id, result)
    }
  } else if (id !== header.id) {
    return result?.push(header.id)
  }
  return result
}

// @ts-ignore
export const headerGroupDependentsStateInitializer: GridStateInitializer<
  Pick<DataGridProcessedProps, 'apiRef'>
> = (state, props, apiRef) => {
  const left = apiRef?.current.tableInstance.getLeftHeaderGroups()
  const right = apiRef?.current.tableInstance.getRightHeaderGroups()
  const center = apiRef?.current.tableInstance.getCenterHeaderGroups()

  return {
    ...state,
    headerGroupDependents: computeAllHeaderGroupDependents({
      left: left,
      right: right,
      center: center,
    }),
  }
}

export const useGridHeaderGroupDependents = (
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>,
  props: Pick<DataGridProcessedProps, 'columns'>
): void => {
  const setHeaderGroupDependents = React.useCallback<
    GridHeaderGroupDependentsApi['setHeaderGroupDependents']
  >(
    ({ left, center, right }): void => {
      apiRef.current.setState(state => {
        const headerGroupDependents = gridHeaderGroupDependentsSelector(state)
        if (
          isDeepEqual(headerGroupDependents.left, left) &&
          isDeepEqual(headerGroupDependents.right, right) &&
          isDeepEqual(headerGroupDependents.center, center)
        ) {
          return state
        }
        return { ...state, headerGroupDependents: { left, center, right } }
      })
      apiRef.current.forceUpdate()
    },
    [apiRef]
  )
  const { columnPinning, columnOrder } = apiRef.current.tableInstance.getState()

  React.useEffect(() => {
    const left = apiRef?.current.tableInstance.getLeftHeaderGroups()
    const right = apiRef?.current.tableInstance.getRightHeaderGroups()
    const center = apiRef?.current.tableInstance.getCenterHeaderGroups()
    apiRef.current.setHeaderGroupDependents(
      computeAllHeaderGroupDependents({
        left: left,
        right: right,
        center: center,
      })
    )
    // we want to recompute header group dependents when columns count column order or column pinning changes
  }, [apiRef, columnPinning, columnOrder, props.columns])
  const headerGroupDependentsApi: GridHeaderGroupDependentsApi = {
    setHeaderGroupDependents,
  }

  useGridApiMethod(apiRef, headerGroupDependentsApi, 'public')
}
